/**
 * ASTRO FORM ENDPOINTS
 * ====================
 * Two calls, both POST through the shared network layer (api/client.ts), so they
 * inherit the same auth headers / timeout / error handling as the feed:
 *
 *   1. getGeoDetails(place)  — city autocomplete for the Place-of-Birth field.
 *   2. postAstroForm(body)   — submit the collected birth details.
 *
 * Paths + request/response shapes mirror vibely-android 1:1 (GetGeoDetailsUseCase
 * / ConsultationAstrologyFormUseCase), on the same mashvara-service the feed
 * already uses. Both paths are verified against the web gateway (VB-2607).
 */
import { apiClient } from './client'

const GEO_DETAILS_PATH = '/mashvara-service/v1/public/geo-details'
const ASTRO_FORM_PATH = '/mashvara-service/v1/public/consultation/astrology/user-profile'

/** Android requests 10 rows; keep parity so the list length matches. */
const GEO_MAX_ROWS = 10

/* ------------------------------------------------------------------ */
/* Geo lookup                                                          */
/* ------------------------------------------------------------------ */

/** One geoname row. We only consume `place_name`; the rest is sent by the API. */
export interface GeoName {
  place_name?: string
  country_code?: string
  latitude?: string
  longitude?: string
  timezone_id?: string
}

export interface GeoDetailsResponse {
  geonames: GeoName[]
}

/**
 * Look up cities for `place`. Returns just the display strings (matching
 * Android, which maps geonames → place names). An empty/short query is fine —
 * the caller (useGeoSearch) handles debounce + min-length; an empty query asks
 * the API for the default top cities.
 */
export async function getGeoDetails(place: string, signal?: AbortSignal): Promise<string[]> {
  const res = await apiClient.post<GeoDetailsResponse>(
    GEO_DETAILS_PATH,
    { place, maxRows: GEO_MAX_ROWS },
    signal
  )
  return (res?.geonames ?? [])
    .map((g) => g.place_name)
    .filter((name): name is string => !!name)
}

/* ------------------------------------------------------------------ */
/* Form submit                                                         */
/* ------------------------------------------------------------------ */

/** Exact submit body (server formats — see validation.ts for the formatters). */
export interface AstroFormSubmit {
  name: string
  /** Single char, per Android. */
  gender: 'M' | 'F'
  /** "DD-MM-YYYY", zero-padded. */
  dateOfBirth: string
  /** "HH:MM AM/PM", or null when not provided. */
  timeOfBirth: string | null
  /** City string, or null when not provided (and not mandatory). */
  placeOfBirth: string | null
}

/** Submit the birth details. Resolves on 200; throws ApiError on failure. */
export async function postAstroForm(body: AstroFormSubmit, signal?: AbortSignal): Promise<void> {
  await apiClient.post<void>(ASTRO_FORM_PATH, body, signal)
}
